import React, { useRef, useState } from "react";
import { useSearch } from "../../context/SearchContext";
import CustomAlert from "../atoms/CustomAlert";
import SuggestedQueries from "./SuggestedQueries";
import SearchInput from "./SearchInput";

interface SearchBarProps {
  queryDisplayMode: "hideSuggestedQueries" | "responsePage" | "searchPage";
  isInternalSearch: boolean;
  isLoading?: boolean;
  onMoreQuestionsClick: () => void;
  onProductosPreaprobadosClick: () => void;
  onSuggestionsEnabledChange?: (enabled: boolean) => void;
  onSearchSubmit?: () => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  queryDisplayMode,
  isInternalSearch,
  isLoading = false,
  onMoreQuestionsClick,
  onSuggestionsEnabledChange,
  onProductosPreaprobadosClick,
  onSearchSubmit,
}) => {
  const { handleSearch, contextReceived, currentSearchTerm } = useSearch();
  const [suggestionsEnabled, setSuggestionsEnabled] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleQueryClick = (query: string) => {
    handleSearch(query, "initial", "menu");
  };

  const handleSuggestionEnabledChange = (enabled: boolean) => {
    setSuggestionsEnabled(enabled);
    if (onSuggestionsEnabledChange) {
      onSuggestionsEnabledChange(enabled);
    }
  };

  const contextConfirmAlert = (
    <div className="max-w-[330px] hidden">
      <CustomAlert message="¡Todo listo! Empieza tu búsqueda personalizada." />
    </div>
  );

  const renderSearchPageComponents = () => {
    if (queryDisplayMode !== "searchPage") return null;

    return (
      <div
        className={`
          ${suggestionsEnabled ? "blur-sm" : ""}
          w-full mx-0 px-0 z-10
        `}
      >
        <SuggestedQueries
          onQueryClick={handleQueryClick}
          onMoreQuestionsClick={onMoreQuestionsClick}
          onProductosPreaprobadosClick={onProductosPreaprobadosClick}
        />

        {contextReceived && contextConfirmAlert}
      </div>
    );
  };

  return (
    <div
      className={`
        ${
          queryDisplayMode === "responsePage" ||
          queryDisplayMode === "searchPage"
            ? "md:relative space-y-2 transition-all duration-300 ease-in-out w-full px-0 mx-0"
            : "w-full px-0 mx-0"
        }`}
      ref={wrapperRef}
    >
      {/* Contenedor principal */}
      <div className="flex flex-col justify-center items-center w-full px-0 mx-0">
        {queryDisplayMode === "searchPage" && (
          <div className="w-full max-w-[680px] px-4 md:px-4 mb-4">
            <h1 className="font-flexo-demi text-[24px] xs:text-[28px] sm:text-[32px] md:text-[40px] leading-[28px] xs:leading-[32px] sm:leading-[40px] md:leading-[48px] text-BCP-dark-blue whitespace-nowrap">
              ¿En qué te puedo ayudar?
            </h1>
          </div>
        )}
        {/* Desktop layout */}
        <div className="flex-col-reverse flex md:flex-col md:w-full md:justify-center md:items-center md:space-y-5 w-full px-0 mx-0">
          {/* Desktop: Search Input Component */}
          <div className="fixed md:static bottom-0 md:bottom-auto left-0 md:left-auto right-0 md:right-auto w-full px-4 md:px-4 rounded-t-[32px] md:rounded-none md:border-t-0 py-3 md:py-0 bg-[#DCEBFE] md:bg-transparent z-20">
            <SearchInput
              isInternalSearch={isInternalSearch}
              isLoading={isLoading}
              currentSearchTerm={currentSearchTerm}
              queryDisplayMode={queryDisplayMode}
              onSuggestionsEnabledChange={handleSuggestionEnabledChange}
              onSearchSubmit={onSearchSubmit}
            />
          </div>
          {/* Desktop: Show suggested queries below the search input */}
          {queryDisplayMode === "searchPage" && (
            <div className="w-full px-0 mx-0">
              {renderSearchPageComponents()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
